import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils/utils';

interface DebouncedInputProps {
    value: string;
    onSave: (val: string) => void;
    placeholder?: string;
    className?: string;
    debounceMs?: number;
}

/**
 * DebouncedInput - An input that manages local state and debounces saves.
 * 
 * This component solves the "typing too fast" problem where rapid onChange
 * events can cause race conditions with async save operations. It maintains
 * local state during typing and only triggers onSave after the user pauses.
 * 
 * @param value - The controlled value from parent
 * @param onSave - Callback fired after debounce period with the new value
 * @param placeholder - Optional placeholder text
 * @param className - Optional className for styling
 * @param debounceMs - Debounce delay in milliseconds (default: 500)
 */
export function DebouncedInput({
    value,
    onSave,
    placeholder,
    className,
    debounceMs = 500
}: DebouncedInputProps) {
    const [localValue, setLocalValue] = useState(value);

    // Sync from parent when value prop changes (e.g., selection change)
    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    // Debounced save effect
    useEffect(() => {
        const timer = setTimeout(() => {
            if (localValue !== value) {
                onSave(localValue);
            }
        }, debounceMs);
        return () => clearTimeout(timer);
    }, [localValue, onSave, value, debounceMs]);

    return (
        <input
            className={cn(
                "flex h-8 w-full rounded-md border border-input bg-transparent px-2 py-1 text-xs shadow-sm transition-colors",
                "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                className
            )}
            placeholder={placeholder}
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
        />
    );
}
